import { useLiveQuery } from 'dexie-react-hooks'
import { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { chargeReelle, nomCategorie, perimetreCategorie, repartitionDe } from '../../analysis/donnees'
import { etatCreance } from '../../analysis/repartition'
import { listerPieces } from '../../db/pieces'
import { somme } from '../../domain/argent'
import { capitaliser, formatJourLong, moisCourant } from '../../domain/temps'
import type { Id, MoisKey } from '../../domain/types'
import {
  Entete,
  IconePlus,
  IconeTrombone,
  Montant,
  NavMois,
  Pastille,
  Puce,
  SelectCategorie,
  SelectCompte,
} from '../composants'
import { useDonnees } from '../donnees'

export function PageOperations() {
  const { d, ix } = useDonnees()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const mois: MoisKey = params.get('mois') ?? moisCourant()
  const compteId = params.get('compte')
  const categorieId = params.get('categorie')
  const [recherche, setRecherche] = useState('')

  const avecPieces = new Set(useLiveQuery(async () => (await listerPieces()).map((p) => p.operationId), []) ?? [])

  const maj = (cle: string, valeur: string | null) => {
    const p = new URLSearchParams(params)
    if (valeur) p.set(cle, valeur)
    else p.delete(cle)
    setParams(p, { replace: true })
  }

  const ops = useMemo(() => {
    const terme = recherche.trim().toLowerCase()
    const perimetre = categorieId ? perimetreCategorie(ix, categorieId) : null
    return d.operations
      .filter(
        (o) =>
          // Une recherche porte sur tout l'historique, pas seulement le mois affiche.
          (terme ? true : o.date.startsWith(mois)) &&
          (!compteId || o.compteId === compteId) &&
          (!perimetre || perimetre.has(o.categorieId)) &&
          (!terme || `${o.libelle} ${o.note} ${nomCategorie(ix, o.categorieId)}`.toLowerCase().includes(terme)),
      )
      .sort((a, b) => b.date.localeCompare(a.date) || b.creeLe.localeCompare(a.creeLe))
  }, [d.operations, ix, mois, compteId, categorieId, recherche])

  const parJour = useMemo(() => {
    const m = new Map<string, typeof ops>()
    for (const o of ops) m.set(o.date, [...(m.get(o.date) ?? []), o])
    return [...m.entries()]
  }, [ops])

  const depensesFlux = somme(ops.filter((o) => o.sens === 'depense').map((o) => o.montant))
  const depensesCharge = somme(ops.filter((o) => o.sens === 'depense').map((o) => chargeReelle(ix, o)))
  const revenus = somme(ops.filter((o) => o.sens === 'revenu').map((o) => o.montant))

  return (
    <>
      <Entete titre="Opérations" sous={recherche ? 'Recherche dans tout l’historique' : capitaliser(new Intl.DateTimeFormat('fr-CH', { month: 'long', year: 'numeric' }).format(new Date(`${mois}-15T12:00:00`)))} />
      <main className="contenu">
        {!recherche && <NavMois mois={mois} onChange={(m) => maj('mois', m)} />}
        <div className="filtres">
          <input type="search" placeholder="Rechercher…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
          <SelectCompte comptes={d.comptes} valeur={compteId} onChange={(v) => maj('compte', v)} tous="Tous les comptes" />
          <SelectCategorie categories={d.categories} valeur={categorieId} onChange={(v) => maj('categorie', v)} toutes="Toutes les catégories" />
        </div>

        <div className="mesures">
          <div className="mesure">
            <div className="mesure__valeur"><Montant valeur={depensesFlux} /></div>
            <div className="mesure__libelle">dépensé</div>
          </div>
          <div className="mesure">
            <div className="mesure__valeur"><Montant valeur={depensesCharge} /></div>
            <div className="mesure__libelle">à ma charge</div>
          </div>
          <div className="mesure">
            <div className="mesure__valeur"><Montant valeur={revenus} /></div>
            <div className="mesure__libelle">revenus</div>
          </div>
        </div>

        {parJour.length === 0 ? (
          <p className="vide">Aucune opération.</p>
        ) : (
          parJour.map(([jour, liste]) => (
            <section key={jour}>
              <h3 className="groupe-date">{formatJourLong(jour)}</h3>
              <ul className="liste">
                {liste.map((o) => (
                  <LigneOperation key={o.id} id={o.id} avecPiece={avecPieces.has(o.id)} />
                ))}
              </ul>
            </section>
          ))
        )}
      </main>
      <button type="button" className="fab" aria-label="Nouvelle opération" onClick={() => navigate(`/operations/nouvelle${compteId ? `?compte=${compteId}` : ''}`)}>
        <IconePlus />
      </button>
    </>
  )
}

function LigneOperation({ id, avecPiece }: { id: Id; avecPiece: boolean }) {
  const { ix } = useDonnees()
  const o = ix.operationParId.get(id)!
  const cat = ix.categorieParId.get(o.categorieId)
  const r = repartitionDe(ix, o)
  const partagee = r.lignes.some((l) => l.part.tiersId !== ix.d.moiId)
  const ouvert = partagee
    ? r.lignes
        .filter((l) => l.part.tiersId !== ix.d.moiId)
        .some((l) => etatCreance(l.part, l.montant, ix.reglementsParPart.get(l.part.id) ?? []).statut !== 'soldee')
    : false

  return (
    <li>
      <Link to={`/operations/${o.id}`} className="item">
        <Pastille couleur={cat?.couleur ?? '#999'} />
        <div className="grandir">
          <div className="item__principal">{o.libelle || nomCategorie(ix, o.categorieId)}</div>
          <div className="item__secondaire ligne ligne--enroulee" style={{ gap: 6 }}>
            <span>{nomCategorie(ix, o.categorieId)} · {ix.compteParId.get(o.compteId)?.nom}</span>
            {partagee && <Puce ton={ouvert ? 'alerte' : 'ok'}>{ouvert ? 'à recevoir' : 'remboursée'}</Puce>}
            {o.recurrenceId && <Puce>récurrente</Puce>}
            {avecPiece && <span className="tres-doux" aria-label="Pièce jointe"><IconeTrombone /></span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Montant valeur={o.montant} sens={o.sens} />
          {partagee && (
            <div className="tres-doux mono">
              ma part <Montant valeur={r.partMoi} />
            </div>
          )}
        </div>
      </Link>
    </li>
  )
}
